import React from 'react';
import {
  VStack,
  Box,
  Heading,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  Badge,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import PresentationExamples from './PresentationExamples';

const StudentsPresentationFeature = () => {
  return (
    <VStack spacing={8} align="stretch">
      {/* Catchy Hook - Always Visible */}
      <Box>
        <Heading as="h2" size="xl" mb={6} color="accent.blue">
          Build. Explain. Get Feedback. Improve.
        </Heading>
        <VStack spacing={5} align="start">
          <Box>
            <Text fontSize="lg" fontWeight="600" mb={2} color="brand.700">
              The key is to make presentation a natural part of learning, not another exam or hurdle.
            </Text>
            <Text color="gray.600" lineHeight="tall">
              Every important exercise is an opportunity to practice not only coding, but also
              explaining what you built.
            </Text>
          </Box>

          <Box bg="blue.50" p={6} borderRadius="lg" borderLeft="4px solid" borderColor="accent.blue">
            <Text fontSize="md" color="brand.800" fontWeight="500" mb={3}>
              You don't need to be a great presenter to start.
            </Text>
            <Text color="gray.700" lineHeight="tall">
              You simply build your solution, prepare a short presentation, walk through your code,
              and run your program. An instructor then gives you simple, specific feedback that helps
              you improve.
            </Text>
          </Box>
        </VStack>
      </Box>

      {/* How It Works - Collapsed */}
      <Accordion allowToggle>
        <AccordionItem border="2px solid" borderColor="gray.300" bg="white" borderRadius="lg" overflow="hidden" _hover={{ borderColor: 'gray.400' }}>
          <AccordionButton
            _expanded={{ bg: 'accent.blue', color: 'white' }}
            py={5}
            px={6}
            fontSize="md"
            fontWeight="600"
          >
            <Box flex="1" textAlign="left">
              How it works?
            </Box>
            <ChevronDownIcon />
          </AccordionButton>

          <AccordionPanel pb={8} pt={8} px={6}>
            <VStack spacing={12} align="start" w="100%">
              {/* A Simple Process */}
              <Box w="100%">
                <Heading as="h3" size="md" mb={6} color="brand.700">
                  A simple process
                </Heading>
                <VStack spacing={4} align="start">
                  {[
                    { num: '1', title: 'Build', desc: 'Work on the exercise and get your program working.' },
                    { num: '2', title: 'Prepare', desc: 'Create a short presentation using a simple structure provided to you. You don\'t have to design slides from scratch. You can even leverage AI tools like Gamma.app to generate the presentation automatically.' },
                    { num: '3', title: 'Present', desc: 'Explain what you built, how it works, and the important parts of your code.' },
                    { num: '4', title: 'Demonstrate', desc: 'Run your program and show the actual result.' },
                    { num: '5', title: 'Get feedback', desc: 'Receive feedback using a simple evaluation rubric.' },
                    { num: '6', title: 'Improve', desc: 'Apply the feedback in your next exercise or presentation.' },
                  ].map((step, idx) => (
                    <HStack key={idx} spacing={4} align="start" w="100%">
                      <Badge
                        bg="accent.blue"
                        color="white"
                        borderRadius="full"
                        minW="40px"
                        h="40px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        fontWeight="bold"
                        fontSize="md"
                      >
                        {step.num}
                      </Badge>
                      <Box flex={1}>
                        <Text fontWeight="600" color="brand.700" mb={1}>{step.title}</Text>
                        <Text color="gray.600" fontSize="sm">{step.desc}</Text>
                      </Box>
                    </HStack>
                  ))}
                </VStack>
              </Box>

              {/* You don't have to be a confident speaker */}
              <Box>
                <Heading as="h4" size="md" mb={4} color="brand.700">
                  You don't have to be a confident speaker
                </Heading>
                <Text fontWeight="600" mb={4} color="accent.blue">
                  This is important.
                </Text>
                <VStack spacing={4} align="start">
                  <Text color="gray.700" lineHeight="tall">
                    Many students hesitate to speak in front of others. Some know the answer but become
                    nervous when asked to explain it.
                  </Text>
                  <Text color="gray.700" lineHeight="tall">
                    We don't expect you to be good at presentations on day one.
                  </Text>
                  <Text color="gray.700" lineHeight="tall">
                    The purpose of these exercises is partly to help you become comfortable explaining
                    your own work.
                  </Text>
                  <Box bg="amber.50" p={4} borderRadius="md" borderLeft="4px solid" borderColor="amber.400">
                    <Text fontWeight="600" mb={3} color="brand.700">
                      You can start small
                    </Text>
                    <VStack spacing={2} align="start" color="gray.700" fontSize="sm">
                      <Text>✓ You can make mistakes.</Text>
                      <Text>✓ You can pause.</Text>
                      <Text>✓ You can forget something.</Text>
                      <Text>✓ You can try again.</Text>
                    </VStack>
                  </Box>
                  <Text fontWeight="600" color="accent.blue" mt={2}>
                    The focus is on improvement, not perfection.
                  </Text>
                </VStack>
              </Box>

              {/* A simple presentation structure */}
              <Box>
                <Heading as="h4" size="md" mb={4} color="brand.700">
                  A simple presentation structure
                </Heading>
                <Text color="gray.700" mb={6} lineHeight="tall">
                  Students don't have to figure out what to say every time. They can follow a consistent
                  structure:
                </Text>
                <VStack spacing={3} align="start">
                  {[
                    { num: '1', title: 'What was the problem?', desc: 'What were you asked to build?' },
                    { num: '2', title: 'What did I build?', desc: 'Briefly describe your solution.' },
                    { num: '3', title: 'How does it work?', desc: 'Explain the important flow or design.' },
                    { num: '4', title: 'How did I implement it?', desc: 'Walk through the important parts of the code.' },
                    { num: '5', title: 'Does it work?', desc: 'Run the program and demonstrate it.' },
                    { num: '6', title: 'What did I learn?', desc: 'Mention one or two things you learned while doing the exercise.' },
                  ].map((item, idx) => (
                    <HStack key={idx} spacing={4} align="start" w="100%">
                      <Badge
                        bg="gray.400"
                        color="white"
                        borderRadius="full"
                        minW="35px"
                        h="35px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        fontWeight="bold"
                      >
                        {item.num}
                      </Badge>
                      <Box flex={1}>
                        <Text fontWeight="600" color="brand.700" mb={1}>{item.title}</Text>
                        <Text color="gray.600" fontSize="sm">{item.desc}</Text>
                      </Box>
                    </HStack>
                  ))}
                </VStack>
                <Text fontStyle="italic" color="gray.600" mt={4} fontSize="sm">
                  That's enough. The structure keeps the presentation focused and makes it easier for
                  students who are unsure about speaking.
                </Text>
              </Box>

              {/* Simple evaluation */}
              <Box>
                <Heading as="h4" size="md" mb={4} color="brand.700">
                  Simple evaluation
                </Heading>
                <Text color="gray.700" mb={6} lineHeight="tall">
                  The evaluation is intentionally simple. It focuses on what we look for:
                </Text>
                <Box overflowX="auto">
                  <Table variant="simple" size="sm">
                    <Thead bg="gray.100">
                      <Tr>
                        <Th color="brand.700" fontWeight="700">Area</Th>
                        <Th color="brand.700" fontWeight="700">What we look for</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      <Tr>
                        <Td fontWeight="600" color="brand.700">Understanding</Td>
                        <Td color="gray.700">Does the student understand the problem?</Td>
                      </Tr>
                      <Tr bg="gray.50">
                        <Td fontWeight="600" color="brand.700">Approach</Td>
                        <Td color="gray.700">Can the student explain the solution?</Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight="600" color="brand.700">Code</Td>
                        <Td color="gray.700">Can the student explain the important code?</Td>
                      </Tr>
                      <Tr bg="gray.50">
                        <Td fontWeight="600" color="brand.700">Working Demo</Td>
                        <Td color="gray.700">Does the program work?</Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight="600" color="brand.700">Explanation</Td>
                        <Td color="gray.700">Can the student make the idea understandable?</Td>
                      </Tr>
                      <Tr bg="gray.50">
                        <Td fontWeight="600" color="brand.700">Q&A</Td>
                        <Td color="gray.700">Can the student respond to basic questions?</Td>
                      </Tr>
                    </Tbody>
                  </Table>
                </Box>
              </Box>

              {/* Feedback becomes part of learning */}
              <Box>
                <Heading as="h4" size="md" mb={4} color="brand.700">
                  Feedback becomes part of learning
                </Heading>
                <Text color="gray.700" mb={6} lineHeight="tall">
                  Instead of simply receiving a mark and moving to the next chapter, students get useful
                  feedback such as:
                </Text>
                <VStack spacing={3} align="start">
                  {[
                    '"Your solution works well. Next time, explain the data flow before showing the code."',
                    '"You understand the code. Try to explain it without reading directly from the slides."',
                    '"The approach is good. Now think about how you could make this part simpler."',
                  ].map((feedback, idx) => (
                    <Box key={idx} bg="green.50" p={4} borderRadius="md" borderLeft="4px solid" borderColor="green.400" w="100%">
                      <Text color="gray.700" fontStyle="italic">{feedback}</Text>
                    </Box>
                  ))}
                </VStack>
                <Text color="gray.700" mt={6} lineHeight="tall">
                  Small improvements accumulate. Over several exercises, students become better at building,
                  explaining and defending their own work.
                </Text>
              </Box>

              {/* Your progress becomes visible */}
              <Box>
                <Heading as="h4" size="md" mb={4} color="brand.700">
                  Your progress becomes visible
                </Heading>
                <Text color="gray.700" mb={4} lineHeight="tall">
                  The same simple evaluation can be used repeatedly. Students can see how they are progressing
                  in areas such as:
                </Text>
                <Box bg="blue.50" p={4} borderRadius="md" mb={4}>
                  <Text color="brand.700" fontWeight="600">
                    Technical understanding → Problem solving → Code → Explanation → Communication → Q&A
                  </Text>
                </Box>
                <Text color="gray.700" lineHeight="tall">
                  The objective isn't to compete with other students. It is to see: "Am I getting better?"
                </Text>
              </Box>

              {/* Your work can become your evidence */}
              <Box>
                <Heading as="h4" size="md" mb={4} color="brand.700">
                  Your work can become your evidence
                </Heading>
                <Text color="gray.700" mb={4} lineHeight="tall">
                  With the student's permission, presentations can be recorded and published. This creates
                  a growing record of the student's work:
                </Text>
                <Box bg="purple.50" p={4} borderRadius="md" mb={4}>
                  <Text color="brand.700" fontWeight="600">
                    Exercise → Code → Presentation → Demonstration → Feedback → Improvement
                  </Text>
                </Box>
                <Text color="gray.700" lineHeight="tall">
                  Later, this can become part of the student's portfolio. Instead of only telling someone:
                </Text>
                <Box bg="gray.100" p={4} borderRadius="md" my={4}>
                  <Text color="gray.800" fontStyle="italic" mb={3}>
                    "I know Python."
                  </Text>
                </Box>
                <Text color="gray.700" lineHeight="tall">
                  The student can show:
                </Text>
                <Box bg="green.50" p={4} borderRadius="md" borderLeft="4px solid" borderColor="green.400">
                  <Text color="gray.800" fontStyle="italic">
                    "Here is something I built. Here is my code. Here is how it works. Here is how I
                    approached the problem."
                  </Text>
                  <Text color="gray.700" mt={2} fontWeight="600">That is useful evidence.</Text>
                </Box>
              </Box>

              {/* No pressure to be perfect */}
              <Box>
                <Heading as="h4" size="md" mb={4} color="brand.700">
                  No pressure to be perfect
                </Heading>
                <VStack spacing={4} align="start">
                  <Text color="gray.700" lineHeight="tall">
                    The first presentation may not be great. That's expected.
                  </Text>
                  <VStack spacing={2} align="start" color="gray.700">
                    <Text>The second should be a little better.</Text>
                    <Text>The fifth should feel easier.</Text>
                    <Text>The tenth should feel natural.</Text>
                  </VStack>
                  <Text color="gray.700" lineHeight="tall">
                    The goal isn't to turn every student into a professional presenter. The goal is to make
                    students comfortable enough to stand behind their work, explain their thinking, answer
                    questions and keep improving.
                  </Text>
                </VStack>
              </Box>

              {/* The learning loop */}
              <Box bg="linear-gradient(135deg, #f5f0ff 0%, #eef2ff 100%)" p={6} borderRadius="lg">
                <Heading as="h4" size="md" mb={4} color="brand.700">
                  The learning loop
                </Heading>
                <Box bg="white" p={4} borderRadius="md" mb={4}>
                  <Text color="brand.700" fontWeight="600" textAlign="center">
                    Build → Explain → Demonstrate → Feedback → Improve → Build again
                  </Text>
                </Box>
                <Box borderLeft="4px solid" borderColor="accent.blue" pl={4}>
                  <Text color="brand.700" fontWeight="600" mb={2}>
                    And there is one simple rule:
                  </Text>
                  <Text color="gray.800" fontWeight="600" fontSize="lg">
                    You don't have to be good at it before you start. You become better by doing it.
                  </Text>
                </Box>
              </Box>
            </VStack>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>

      {/* See It In Action - Collapsed */}
      <Accordion allowToggle>
        <AccordionItem border="2px solid" borderColor="gray.300" bg="white" borderRadius="lg" overflow="hidden" _hover={{ borderColor: 'gray.400' }}>
          <AccordionButton
            _expanded={{ bg: 'accent.blue', color: 'white' }}
            py={5}
            px={6}
            fontSize="md"
            fontWeight="600"
          >
            <Box flex="1" textAlign="left">
              See it in action
            </Box>
            <ChevronDownIcon />
          </AccordionButton>

          <AccordionPanel pb={8} pt={8} px={6}>
            <PresentationExamples />
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </VStack>
  );
};

export default StudentsPresentationFeature;
